import { useEffect, useRef, useState } from "react";
import { GlassCard, Button } from "@/shared/ui";
import { useAuthConfig, useGoogleAuth, useLogin, useSendCode, useSignup, useSendResetCode, useResetPassword } from "@/shared/api/hooks";
import { useI18n } from "@/shared/i18n";
import { ApiError } from "@/shared/api/client";

type Mode = "login" | "signup" | "reset";

/** Minimal surface of Google Identity Services we use (loaded from gsi/client). */
interface GsiId {
  initialize(cfg: {
    client_id: string;
    callback: (res: { credential: string }) => void;
  }): void;
  renderButton(el: HTMLElement, cfg: Record<string, unknown>): void;
}

declare global {
  interface Window {
    google?: { accounts: { id: GsiId } };
  }
}

const GSI_SRC = "https://accounts.google.com/gsi/client";

/** Load the GIS script once and resolve with the `google.accounts.id` API. */
function loadGsi(): Promise<GsiId> {
  return new Promise((resolve, reject) => {
    const ready = () => window.google && resolve(window.google.accounts.id);
    if (window.google) return ready();
    let s = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
    if (!s) {
      s = document.createElement("script");
      s.src = GSI_SRC;
      s.async = true;
      document.head.appendChild(s);
    }
    s.addEventListener("load", ready);
    s.addEventListener("error", () => reject(new Error("gsi load failed")));
  });
}

interface Props {
  /** Called after a successful signup with the one-time plaintext api_key. */
  onSignedUp?: (apiKey: string) => void;
}

export function AuthPanel({ onSignedUp }: Props) {
  const { t } = useI18n();
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const login = useLogin();
  const signup = useSignup();
  const signupCode = useSendCode();
  const resetCode = useSendResetCode();
  const resetPassword = useResetPassword();
  const sendCode = mode === "reset" ? resetCode : signupCode;
  const [message, setMessage] = useState<string | null>(null);
  const googleAuth = useGoogleAuth();
  const authConfig = useAuthConfig();
  // Until the flag loads, assume no code needed; the server enforces anyway.
  const needsCode = mode === "reset" || (mode === "signup" && authConfig.data?.email_verification === true);
  const busy = login.isPending || signup.isPending || googleAuth.isPending || resetPassword.isPending;

  const googleClientId = authConfig.data?.google_client_id;
  const googleBtnRef = useRef<HTMLDivElement | null>(null);
  // Keep the latest mutation in a ref so the GIS callback (registered once)
  // never captures a stale closure.
  const googleAuthRef = useRef(googleAuth);
  googleAuthRef.current = googleAuth;
  const onSignedUpRef = useRef(onSignedUp);
  onSignedUpRef.current = onSignedUp;

  useEffect(() => {
    if (!googleClientId || !googleBtnRef.current) return;
    let cancelled = false;
    const el = googleBtnRef.current;
    loadGsi()
      .then((gsi) => {
        if (cancelled) return;
        gsi.initialize({
          client_id: googleClientId,
          callback: async ({ credential }) => {
            setError(null);
            try {
              const res = await googleAuthRef.current.mutateAsync(credential);
              // A brand-new account gets its one-time API key, like signup.
              if (res.api_key) onSignedUpRef.current?.(res.api_key);
            } catch (e) {
              setError(e instanceof ApiError ? t(e.message) : t("Network error, please retry"));
            }
          },
        });
        gsi.renderButton(el, {
          theme: "outline",
          size: "large",
          text: "continue_with",
          width: 320,
        });
      })
      .catch(() => {
        // Script blocked (adblock / offline) — email+password still works.
      });
    return () => {
      cancelled = true;
    };
    // t is stable enough for error text; re-render on client id only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleClientId]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  const emailOk = email.trim().length > 0;
  const pwOk = password.length >= 8;
  const codeOk = !needsCode || code.trim().length === 6;
  const canSubmit = emailOk && pwOk && codeOk && !busy;

  async function onSendCode() {
    setError(null);
    if (!emailOk) return setError(t("Enter your email"));
    try {
      await sendCode.mutateAsync(email);
      setCooldown(60);
      if (mode === "reset") setMessage(t("If this email has a password account, a reset code has been sent."));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setError(t("This email is already registered — sign in instead"));
      } else if (e instanceof ApiError && e.status === 503) {
        setError(t("A code was just sent — check your inbox or wait a minute"));
      } else {
        setError(e instanceof ApiError ? t(e.message) : t("Network error, please retry"));
      }
    }
  }

  function mapError(e: unknown): string {
    if (!(e instanceof ApiError)) return t("Network error, please retry");
    if (mode === "login" && e.status === 401) return t("Wrong email or password");
    if (mode === "signup" && e.status === 409)
      return t("This email is already registered — sign in instead");
    if (mode === "reset") return t(e.message);
    if (e.status === 400) {
      // Verification-code errors carry their own actionable message.
      if (/verification code/i.test(e.message)) return t(e.message);
      return t("Check the email format and password (min 8 chars)");
    }
    return t(e.message);
  }

  async function submit() {
    setError(null);
    if (!emailOk) return setError(t("Enter your email"));
    if (!pwOk) return setError(t("At least 8 characters"));
    if (!codeOk) return setError(t("Enter the 6-digit code from your email"));
    try {
      if (mode === "login") {
        await login.mutateAsync({ email, password });
      } else if (mode === "reset") {
        await resetPassword.mutateAsync({ email, password, code: code.trim() });
        setMode("login");
        setPassword("");
        setCode("");
        setMessage(t("Password updated. Sign in with your new password; previous sessions have been signed out."));
      } else {
        const res = await signup.mutateAsync({
          email,
          password,
          code: needsCode ? code.trim() : undefined,
        });
        onSignedUp?.(res.api_key);
      }
      // On success the session JWT is set; this panel unmounts.
    } catch (e) {
      setError(mapError(e));
    }
  }

  function switchMode(m: Mode) {
    setMode(m);
    setError(null);
    setMessage(null);
    setCode("");
    setCooldown(0);
  }

  return (
    <GlassCard
      className="auth"
      title={mode === "reset" ? t("Reset password") : mode === "login" ? t("Sign in to your account") : t("Create an account")}
    >
      <div className="auth__tabs">
        <button
          className={`auth__tab ${mode === "login" ? "auth__tab--active" : ""}`}
          onClick={() => switchMode("login")}
        >
          {t("Sign in")}
        </button>
        <button
          className={`auth__tab ${mode === "signup" ? "auth__tab--active" : ""}`}
          onClick={() => switchMode("signup")}
        >
          {t("Sign up")}
        </button>
      </div>

      <div className="auth__field">
        <label className="field-label" htmlFor="auth-email">
          {t("Email")}
        </label>
        <input
          id="auth-email"
          className="input"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div className="auth__field">
        <label className="field-label" htmlFor="auth-pw">
          {t("Password")}
        </label>
        <input
          id="auth-pw"
          className="input"
          type="password"
          autoComplete={mode === "login" ? "current-password" : "new-password"}
          placeholder={t("At least 8 characters")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canSubmit) submit();
          }}
        />
        {mode === "signup" && (
          <div className="auth__hint">
            {t("You'll get an API key after signing up (shown only once).")}
          </div>
        )}
      </div>

      {needsCode && (
        <div className="auth__field">
          <label className="field-label" htmlFor="auth-code">
            {t("Email verification code")}
          </label>
          <div className="auth__code-row">
            <input
              id="auth-code"
              className="input mono"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />
            <Button
              variant="ghost"
              onClick={onSendCode}
              loading={sendCode.isPending}
              disabled={!emailOk || cooldown > 0 || sendCode.isPending}
            >
              {cooldown > 0 ? t("Resend in {s}s", { s: cooldown }) : t("Send code")}
            </Button>
          </div>
        </div>
      )}

      {message && <div className="auth__hint" role="status">{message}</div>}
      {error && <div className="auth__error">{error}</div>}

      <Button onClick={submit} loading={busy} disabled={!canSubmit} block>
        {mode === "reset" ? t("Reset password") : mode === "login" ? t("Sign in") : t("Sign up & sign in")}
      </Button>

      {mode === "login" && authConfig.data?.email_verification && (
        <Button variant="ghost" onClick={() => switchMode("reset")}>{t("Forgot password?")}</Button>
      )}
      {googleClientId && (
        <>
          <div className="auth__divider">
            <span>{t("or")}</span>
          </div>
          <div className="auth__google" ref={googleBtnRef} />
        </>
      )}
    </GlassCard>
  );
}
